/**
 * StepFooter Component
 * 
 * Footer for individual step cards with:
 * - Previous button (left)
 * - Compact Prompt Editor (center) - only for Step 2 (Requirements)
 * - Next button (right)
 * 
 * Following SOLID principles and mobile-first approach
 */

import React from 'react';
import { Button, Flex } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useFormContext } from 'react-hook-form';
// import PromptEditor from '../PromptEditor'; // Commented out for this release, to be considered in next release
import { PersistenceStatus } from '../../hooks/useFormPersistence';
import { JobPostFormData } from '../../schemas/jobPostValidation';
import './index.scss';

interface StepFooterProps {
  /** Current active step (0-6) */
  currentStep: number;
  /** Total number of steps */
  totalSteps: number;
  /** Current prompt value */
  prompt: string;
  /** Whether AI generation is in progress */
  isGenerating: boolean;
  /** Callback for prompt change */
  onPromptChange: (value: string) => void;
  /** Callback for AI generation */
  onGenerate: () => void;
  /** Callback for previous step */
  onPrevious: () => void;
  /** Callback for next step */
  onNext: () => void;
  /** Whether previous button should be disabled */
  isPreviousDisabled?: boolean;
  /** Whether next button should be disabled */
  isNextDisabled?: boolean;
  /** Step titles for dynamic navigation labels */
  stepTitles: string[];
  /** Whether navigation is in progress */
  isNavigating?: boolean;
  /** Current save status */
  saveStatus: PersistenceStatus;
  /** Number of history entries (for Step 2) */
  historyCount?: number;
  /** Callback for history button click (for Step 2) */
  onHistoryClick?: () => void;
}

const StepFooter: React.FC<StepFooterProps> = ({
  currentStep,
  totalSteps,
  prompt,
  isGenerating,
  onPromptChange,
  onGenerate,
  onPrevious,
  onNext,
  isPreviousDisabled = false,
  isNextDisabled = false,
  stepTitles,
  isNavigating = false,
  saveStatus,
  historyCount,
  onHistoryClick,
}) => {
  const { watch } = useFormContext<JobPostFormData>();
  
  // Check if we're on the last step
  const isLastStep = currentStep >= totalSteps - 1;
  
  // Prompt editor should only appear for Step 2 (Requirements) - Commented out for this release, to be considered in next release
  // const shouldShowPromptEditor = currentStep === 2;
  
  // Get step titles for navigation buttons
  const getPreviousStepTitle = () => {
    if (currentStep <= 1) return 'AI Generator';
    return stepTitles[currentStep - 1] || 'Previous';
  };
  
  const getNextStepTitle = () => {
    if (isLastStep) return 'Review';
    
    // Step 4: Job Alignment (currentStep === 4) - NOT Interview Rounds (currentStep === 3)
    // Conditional button text logic only applies to Job Alignment step
    if (currentStep === 4) {
      const step4Data = watch('step4');
      const aiGenerated = step4Data?.jobAlignment?.isAIGenerated ?? true;
      const hasManualQuestions = (step4Data?.manualScreeningQuestions?.length ?? 0) > 0;
      
      // 5.1: aiGenerated: true, no screening questions -> "Save & Proceed"
      if (aiGenerated && !hasManualQuestions) return 'Save & Proceed';
      
      // 5.2: aiGenerated: true, screening questions with topic "manual_questions" -> "Save & Proceed"
      if (aiGenerated && hasManualQuestions) return 'Save & Proceed';
      
      // 5.3: aiGenerated: false, no screening questions -> "Skip & Proceed"
      if (!aiGenerated && !hasManualQuestions) return 'Skip & Proceed';
      
      // 5.4: aiGenerated: false, screening questions with topic "manual_questions" -> "Save & Proceed"
      if (!aiGenerated && hasManualQuestions) return 'Save & Proceed';
    }
    
    return stepTitles[currentStep + 1] || 'Next';
  };
  const isFirstStep = currentStep <= 0;

  return (
    <div className="step-footer">
      <Flex justify="space-between" align="center" gap={16} wrap="wrap">
        {/* Left Section: Previous Button */}
        <div className="step-footer-left">
          <Button
            type="text"
            icon={<LeftOutlined />}
            onClick={onPrevious}
            disabled={isPreviousDisabled || isFirstStep || isNavigating}
            loading={isNavigating && saveStatus.status === 'saving'}
            className="navigation-button previous-button"
            aria-label="Previous step"
          >
            <span className="button-text">
              {isNavigating && saveStatus.status === 'saving' ? 'Saving...' : getPreviousStepTitle()}
            </span>
          </Button>
        </div>

        {/* Center Section: Compact Prompt Editor - Only show for Step 2 (Requirements) - Commented out for this release, to be considered in next release
        {shouldShowPromptEditor && (
          <div className="step-footer-center">
            <PromptEditor
              prompt={prompt}
              onPromptChange={onPromptChange}
              onGenerate={onGenerate}
              isGenerating={isGenerating}
              maxRows={1}
              placeholder="Refine requirements, skills, and qualifications..."
              historyCount={historyCount}
              onHistoryClick={onHistoryClick}
            />
          </div>
        )}
        */}

        {/* Right Section: Next Button */}
        <div className="step-footer-right">
          <Button
            type="primary"
            onClick={onNext}
            disabled={isNextDisabled || isGenerating || isNavigating}
            loading={isNavigating && saveStatus.status === 'saving'}
            className="navigation-button next-button"
            aria-label={isLastStep ? "Submit" : "Next step"}
          >
            <span className="button-text">
              {isNavigating && saveStatus.status === 'saving' 
                ? 'Saving...' 
                : getNextStepTitle()}
            </span>
            {!isNavigating && <RightOutlined />}
          </Button>
        </div>
      </Flex>
    </div>
  );
};

export default StepFooter;

